import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useOmniboxEnrichment } from "@/contexts/OmniboxEnrichmentContext.tsx";
import type { FlowStep, TextSegment } from "../../typeahead/types.ts";
import {
  CONTEXT_SUGGESTIONS,
  useGuidedFlow,
  type UseGuidedFlowReturn,
} from "../../typeahead/useGuidedFlow.ts";

export { CONTEXT_SUGGESTIONS };

interface UseGuidedFlowIntegrationOptions {
  /** Current textarea value. */
  value: string;
  /** Parent onChange handler. */
  onChange: (value: string) => void;
  /** Ref to the textarea element (for focus management). */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Ref set to `true` by the parent when a value change originates from user typing. */
  isUserInputRef: React.MutableRefObject<boolean>;
  /** The value present at mount is the user's own text, never a prefill to expand. */
  initialValueIsTyped?: boolean;
}

interface UseGuidedFlowIntegrationReturn {
  /** The underlying guided flow hook return. */
  guidedFlow: UseGuidedFlowReturn;
  /** Overlay segments built from the current value, with validated link ranges. */
  guidedFlowOverlaySegments: TextSegment[];
  /** Whether the guided flow overlay should be shown. */
  hasGuidedFlowOverlay: boolean;
  /** Plain text derived from guided flow segments (for dropdown positioning). */
  guidedFlowComposedText: string;
  /** Character offset for dropdown positioning. */
  guidedFlowCharOffset: number;
  /** Ref that is `true` during paste events (flow detection should be skipped). */
  isPastingRef: React.MutableRefObject<boolean>;
  /** Whether the guided flow is currently active. */
  isOpen: boolean;
  /** Dismiss the guided flow. */
  close: () => void;
  /** Handle keyboard events. Returns true if handled. */
  onKeyDown: (e: React.KeyboardEvent) => boolean;
}

/**
 * Integrates the guided scheduling flow with SmartTextarea.
 *
 * Encapsulates all guided-flow-specific concerns:
 * - Wraps `useGuidedFlow` with a value-tracking `onTextUpdate` callback
 * - Detects external prefills (canned prompt chips) and triggers the flow
 * - Returns focus to textarea when the guided flow dropdown closes
 * - Resets the flow when text is cleared after "sent"
 * - Computes overlay segments, composed text, and dropdown char offset
 *
 * This keeps SmartTextarea focused on textarea behavior and overlay rendering.
 */
export function useGuidedFlowIntegration({
  value,
  onChange,
  textareaRef,
  isUserInputRef,
  initialValueIsTyped = false,
}: UseGuidedFlowIntegrationOptions): UseGuidedFlowIntegrationReturn {
  // --- Value tracking for loop-safe prefill detection ---
  // Instead of a boolean ref flag that can be consumed too early (causing
  // infinite loops), we track the last value produced by the guided flow.
  // If the incoming `value` matches, we know it came from the flow and skip.
  // Typed text present at mount is seeded here so the prefill check skips it
  // however many times the mount effect runs.
  const lastFlowValueRef = useRef<string | null>(initialValueIsTyped ? value : null);

  const handleFlowTextUpdate = useCallback(
    (composedText: string) => {
      lastFlowValueRef.current = composedText;
      onChange(composedText);
    },
    [onChange],
  );

  // Track paste events so flow detection is skipped on paste
  const isPastingRef = useRef(false);

  // Any active enrichment can declare it has already answered a scheduling-flow
  // field (e.g. the calendar enrichment sets `provides.when` when ranges are
  // drawn). No enrichment-specific knowledge here — we just fold provides
  // across all active enrichments.
  const { enrichments } = useOmniboxEnrichment();
  const externalInputs = useMemo(
    () => ({
      whenProvided: enrichments.some((e) => e.provides?.when),
      durationProvided: enrichments.some((e) => e.provides?.duration),
    }),
    [enrichments],
  );

  const guidedFlow = useGuidedFlow({
    onTextUpdate: handleFlowTextUpdate,
    externalInputs,
  });

  // --- Prefill detection ---
  // Detect external prefills (e.g. canned prompt chips) and trigger the flow.
  // Fires synchronously before paint so there's no flash of un-styled text.
  // Skips when the value change came from user typing (isUserInputRef) or from
  // the flow itself (value === lastFlowValueRef).
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to value changes
  useLayoutEffect(() => {
    if (isUserInputRef.current) {
      isUserInputRef.current = false;
      return;
    }
    // Skip if this value was produced by the flow itself.
    if (value === lastFlowValueRef.current) return;
    if (!value.trim()) {
      lastFlowValueRef.current = null;
      return;
    }
    // If the flow is already active, reset it first so the new prefill takes over.
    if (guidedFlow.state.step !== "idle") {
      guidedFlow.reset();
      lastFlowValueRef.current = null;
    }
    guidedFlow.prefill(value);
  }, [value]);

  // --- Focus management ---
  // Return focus to textarea when the guided flow dropdown closes (step goes from active to inactive).
  // Prevents focus from falling to document.body when the portal-rendered dropdown unmounts.
  const prevActiveRef = useRef(guidedFlow.isActive);
  useEffect(() => {
    if (prevActiveRef.current && !guidedFlow.isActive && textareaRef.current) {
      textareaRef.current.focus({ preventScroll: true });
    }
    prevActiveRef.current = guidedFlow.isActive;
  }, [guidedFlow.isActive, textareaRef]);

  // --- Sent-step reset ---
  // Only fully reset the flow when the text is cleared (e.g., parent cleared after send).
  // Minor edits during "sent" preserve the state — the overlay uses per-segment
  // validation to show/hide individual links based on what's still intact.
  const prevStepRef = useRef(guidedFlow.state.step);
  useEffect(() => {
    const prevStep = prevStepRef.current;
    prevStepRef.current = guidedFlow.state.step;

    if (guidedFlow.state.step !== "sent" || prevStep !== "sent") return;
    if (value === "") {
      guidedFlow.reset();
      lastFlowValueRef.current = null;
    }
  }, [value, guidedFlow.state.step, guidedFlow.reset]);

  // --- Overlay segments ---
  // Derive the composed text from segments so getDropdownPosition can measure against
  // the up-to-date string instead of the possibly-stale textarea DOM value.
  const guidedFlowComposedText = useMemo(
    () => guidedFlow.segments.map((s) => s.text).join(""),
    [guidedFlow.segments],
  );

  // Build overlay segments from the actual `value`, applying link styles only for
  // segment ranges whose text is still intact at their expected positions.
  const guidedFlowOverlaySegments = useMemo((): TextSegment[] => {
    if (guidedFlow.segments.length === 0) return [];

    // Build expected ranges for linked segments
    const linkRanges: Array<{
      start: number;
      end: number;
      step: FlowStep;
      text: string;
    }> = [];
    let pos = 0;
    for (const seg of guidedFlow.segments) {
      if (seg.step) {
        linkRanges.push({
          start: pos,
          end: pos + seg.text.length,
          step: seg.step,
          text: seg.text,
        });
      }
      pos += seg.text.length;
    }

    if (linkRanges.length === 0) return [];

    // Keep only ranges whose text is still intact in the current value
    const validRanges = linkRanges.filter(
      (range) => value.length >= range.end && value.slice(range.start, range.end) === range.text,
    );

    if (validRanges.length === 0) return [];

    // Build segments from `value` itself, splitting at valid link boundaries
    const result: TextSegment[] = [];
    let cursor = 0;
    for (const range of validRanges) {
      if (cursor < range.start) {
        result.push({ text: value.slice(cursor, range.start) });
      }
      result.push({
        text: value.slice(range.start, range.end),
        step: range.step,
      });
      cursor = range.end;
    }
    if (cursor < value.length) {
      result.push({ text: value.slice(cursor) });
    }

    return result;
  }, [guidedFlow.segments, value]);

  const hasGuidedFlowOverlay =
    guidedFlowOverlaySegments.length > 0 && guidedFlowOverlaySegments.some((s) => s.step);

  // Compute character offset so the dropdown aligns with the current step's segment.
  const guidedFlowCharOffset = useMemo(() => {
    if (guidedFlow.matchContext) return guidedFlow.matchContext.startIndex;
    if (guidedFlow.segments.length === 0) return 0;
    let offset = 0;
    for (const seg of guidedFlow.segments) {
      if (seg.step === guidedFlow.state.step) break;
      offset += seg.text.length;
    }
    return offset;
  }, [guidedFlow.matchContext, guidedFlow.segments, guidedFlow.state.step]);

  const isOpen = guidedFlow.isActive;
  const close = guidedFlow.dismiss;

  const guidedFlowStep = guidedFlow.state.step;
  const guidedFlowRef = guidedFlow.ref;

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!isOpen || !guidedFlowRef.current) return false;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        guidedFlowRef.current.handleArrowDown();
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        guidedFlowRef.current.handleArrowUp();
        return true;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const wasSelected = guidedFlowRef.current.handleEnter();
        if (wasSelected) return true;
      }
      if (e.key === "Tab" && guidedFlowStep === "matching") {
        e.preventDefault();
        if (e.shiftKey) {
          guidedFlowRef.current.handleShiftTab();
        } else {
          guidedFlowRef.current.handleTab();
        }
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return true;
      }
      return false;
    },
    [isOpen, guidedFlowRef, guidedFlowStep, close],
  );

  return {
    guidedFlow,
    guidedFlowOverlaySegments,
    hasGuidedFlowOverlay,
    guidedFlowComposedText,
    guidedFlowCharOffset,
    isPastingRef,
    isOpen,
    close,
    onKeyDown,
  };
}
